import type { BankAccountResource } from "../../bank-account";
import type { CountryResource } from "../../country";
import type { VariantResource } from "../../variant";
import type { PaymentMethodResource } from "../../payment-method";
import type { SupplierResource } from "../../supplier";
import type { CustomerResource } from "../../customer";
import type { AccountingChartResource } from "../../accounting-chart";

export enum AccountingAccountTypeEnum {
  Receivables = "Receivables", // als "Forderungen" Konto für alle Kunden
  Payables = "Payables", // als "Verbindlichkeiten" Konto für alle Lieferanten
  RevenueDomestic = "RevenueDomestic", // als Erlöskonto für Inland
  Expenditure = "Expenditure", // als Ausgaben
  RevenueOther = "RevenueOther", // als sonstige Erlöse
  RevenueEuPrivate = "RevenueEuPrivate", // als Erlöskonto für EU (Privatperson)
  RevenueEuBusiness = "RevenueEuBusiness", // als Erlöskonto für Innergemeinschaftliche Lieferung
  RevenueNonEU = "RevenueNonEU", // als Erlöskonto für nicht EU
  VatDomestic = "VatDomestic", // als MwSt-Konto für Inland
  VatEuPrivate = "VatEuPrivate", // als MwSt-Konto für EU
  ShippingDomestic = "ShippingDomestic", // als Versandkosten für Inland
  ShippingEuPrivate = "ShippingEuPrivate", // als Versandkosten für EU (Privatperson)
  ShippingEuBusiness = "ShippingEuBusiness", // als Versandkosten für Innergemeinschaftliche Lieferung
  ShippingNonEU = "ShippingNonEU", // als Versandkosten für nicht EU
  BankTransactionCosts = "BankTransactionCosts", // als Kosten für Geldverkehr
  AdvancePaymentsReceived = "AdvancePaymentsReceived", // als erhaltene Anzahlungen
  AdvancePaymentsPaid = "AdvancePaymentsPaid", // als geleistete Anzahlungen
  // S2a: typed accounts the AccountResolver needs (no account numbers in business code)
  ExpenseDomestic = "ExpenseDomestic", // Aufwand/Wareneingang Inland mit Vorsteuersatz (expense_kind)
  VatInput = "VatInput", // Vorsteuerkonto mit Satz
  CashBank = "CashBank", // Bank-/Kassen-/Zahlungskonto
  ClearingAccount = "ClearingAccount", // Durchlaufende Posten / Clearing
  OpeningBalanceTransfer = "OpeningBalanceTransfer", // Systemkonto: nur Open-Item-Übernahme
  // S6b: recurring expenses and direct payments for bank booking suggestions
  RentExpense = "RentExpense",
  LeasingExpense = "LeasingExpense",
  InsuranceExpense = "InsuranceExpense",
  TelephoneAndInternetExpense = "TelephoneAndInternetExpense",
  HostingAndSoftwareExpense = "HostingAndSoftwareExpense",
  AdvertisingExpense = "AdvertisingExpense",
  FeesAndContributions = "FeesAndContributions",
  PaymentProviderFees = "PaymentProviderFees",
  WagesAndSalaries = "WagesAndSalaries",
  SocialSecurityExpense = "SocialSecurityExpense",
  PayrollTaxPayment = "PayrollTaxPayment",
  CorporateTaxPayment = "CorporateTaxPayment",
  TradeTaxPayment = "TradeTaxPayment",
  VatPayment = "VatPayment",
}

/** Semantic kind of an expense account; the OCR/semantic classifier returns it, never an account number. */
export enum ExpenseKindEnum {
  Goods = "goods",
  Services = "services",
  Asset = "asset",
  Insurance = "insurance",
  Telecommunication = "telecommunication",
  Software = "software",
  Advertising = "advertising",
  Rent = "rent",
  Leasing = "leasing",
  FeesAndContributions = "fees_and_contributions",
  BankCharges = "bank_charges",
  Other = "other",
}

/** Tax treatment an account proposes by default (S1.5 reference table). The UStVA never reads it from the account. */
export enum VatTaxTypeEnum {
  Domestic = "domestic",
  DomesticZeroRate12Abs3 = "domestic_zero_rate_12_3",
  DomesticZeroUnclassified = "domestic_zero_unclassified",
  EuB2b = "eu_b2b",
  EuB2cOss = "eu_b2c_oss",
  EuB2cDomesticTaxed = "eu_b2c_domestic_taxed",
  EuNewVehicleNoVatId = "eu_new_vehicle_no_vat_id",
  ExportThirdCountry = "export_third_country",
  ExemptWithDeductionOther = "exempt_with_deduction_other",
  ExemptWithoutDeduction = "exempt_without_deduction",
  ReverseChargeSupplied = "reverse_charge_supplied",
  TriangularFirstCustomer = "triangular_first_customer",
  NonTaxableEuService18b = "non_taxable_eu_service_18b",
  NonTaxableForeignPlaceOther = "non_taxable_foreign_place_other",
  NonTaxableInternal = "non_taxable_internal",
  NonTaxableCompensation = "non_taxable_compensation",
  InputDomestic = "input_domestic",
  InputImport = "input_import",
  InputEuAcquisition = "input_eu_acquisition",
  InputEuAcquisitionZeroRate = "input_eu_acquisition_zero_rate",
  InputEuAcquisitionExempt = "input_eu_acquisition_exempt",
  ReverseChargeReceivedEuService13b1 = "reverse_charge_received_eu_service_13b_1",
  ReverseChargeReceivedRealEstate13bAbs2Nr3 = "reverse_charge_received_real_estate_13b_2_3",
  ReverseChargeReceivedOther13b2 = "reverse_charge_received_other_13b_2",
  OtherSpecial = "other_special",
}

export enum VatExemptionTypeEnum {
  None = "none",
  IgDelivery4Nr1b = "ig_delivery_4_1b",
  Export4Nr1a = "export_4_1a",
  Exempt4Nr2To7 = "exempt_4_2_to_7",
  Exempt4Nr8To29 = "exempt_4_8_to_29",
  ZeroRate12Abs3 = "zero_rate_12_3",
  NonTaxablePlaceAbroad = "non_taxable_place_abroad",
  NonTaxableNoExchange = "non_taxable_no_exchange",
  ReverseCharge13b = "reverse_charge_13b",
  Other = "other",
}

export interface AccountingAccountResource {
  id: string;
  gid: string;
  is_important?: boolean;
  /** null = legacy account of an owner without chart (pre-S2a behaviour). */
  chart_id?: string | null;
  chart?: AccountingChartResource | null;
  is_active?: boolean;
  /** System accounts (OpeningBalanceTransfer) are never offered for manual postings. */
  is_system_account?: boolean;
  expense_kind?: ExpenseKindEnum | null;
  default_tax_type?: VatTaxTypeEnum | null;
  default_tax_exemption_type?: VatExemptionTypeEnum | null;
  default_legal_basis?: string | null;
  account_number: string;
  title: string;
  accounting_category: AccountingAccountCategoryEnum;
  type: AccountingAccountTypeEnum | null;
  vat_percent: string | null;
  vat_accounting_account_id: string | null;
  vat_accounting_account?: AccountingAccountResource;
  country_id: string | null;
  country: CountryResource | null;
  customer_id: string | null;
  supplier_id: string | null;
  variants: VariantResource[] | null;
  payment_methods: PaymentMethodResource[] | null;
  bankAccounts?: BankAccountResource[];
  supplier?: SupplierResource | null;
  customer?: CustomerResource | null;
}

export enum AccountingAccountCategoryEnum {
  Depreciation = "Depreciation",
  FixedAssets = "FixedAssets",
  Expenditure = "Expenditure",
  Inventory = "Inventory",
  Income = "Income",
  Demands = "Demands",
  CashBank = "Cash/Bank",
  NonDeductibleExpenses = "NonDeductibleExpenses",
  Private = "Private",
  Provisions = "Provisions",
  Other = "Other",
  Vat = "Vat",
  Liabilities = "Liabilities",
  InputTax = "InputTax",
}
